import { PayloadAction } from '@reduxjs/toolkit';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { TApiFetchResponse } from 'web_core_library';
import { getEvents, getFilter, getPresenterFilter } from './selectors';
import GalleryService from './service';
import { AppActions } from './slice';
import { IEvent } from './types';
import { convertJsonEventListToIevent, filterEvents, getUniqueCategories, sanitizeEventUrls } from './utils';

export function* startAppSaga() {
  // Do some initial setup and load the data
  yield call(GalleryService.init);

  let events: IEvent[] = [];
  try {
    const response: TApiFetchResponse<typeof GalleryService.getEventsJson> = yield call(GalleryService.getEventsJson);
    events = convertJsonEventListToIevent(response.data);
  } catch (error) {
    console.error(error);
  }

  // Sanitize all event URLs to ensure they are safe before using them in the app
  events = yield call(sanitizeEventUrls, events);

  const categories = getUniqueCategories(events);

  // Check URL for category parameter
  const urlParams = new URLSearchParams(window.location.search);
  const categoryFromUrl = urlParams.get('category') || undefined;

  const filteredEvents = filterEvents(events, categoryFromUrl);
  yield put(AppActions.started({ events, categories, filteredEvents }));

  // Set the category filter from URL if present
  if (categoryFromUrl) {
    yield put(AppActions.filterChanged(categoryFromUrl));
  }
}

export function* filterChangedSaga(action: PayloadAction<string | undefined>) {
  const events = yield select(getEvents);
  const categoryFilter = action.payload;
  const presenterFilter = yield select(getPresenterFilter);
  const filteredEvents = filterEvents(events, categoryFilter, presenterFilter);
  yield put(AppActions.setFilteredEvents(filteredEvents));

  // Update URL with category parameter
  const urlParams = new URLSearchParams(window.location.search);
  if (categoryFilter) {
    urlParams.set('category', categoryFilter);
  } else {
    urlParams.delete('category');
  }

  const newUrl = `${window.location.pathname}${urlParams.toString() ? '?' + urlParams.toString() : ''}`;
  window.history.replaceState({}, '', newUrl);
}

export function* presenterFilterChangedSaga(action: PayloadAction<string | undefined>) {
  const events = yield select(getEvents);
  const presenterFilter = action.payload;
  const categoryFilter = yield select(getFilter);
  const filteredEvents = filterEvents(events, categoryFilter, presenterFilter);
  yield put(AppActions.setFilteredEvents(filteredEvents));
}

export default function* appSagaWatcher() {
  yield all([
    takeLatest(AppActions.startApp, startAppSaga),
    takeLatest(AppActions.filterChanged, filterChangedSaga),
    takeLatest(AppActions.presenterFilterChanged, presenterFilterChangedSaga),
  ]);
}
